export default defineNuxtPlugin((nuxtApp) => {
  const { isOnline, markOnline, verifyConnectivity } = useConnectivity();

  // How often to re-check while we believe we are offline. The browser fires no 'online'
  // event when its own flag was stuck, so without this an idle page has no way back.
  const RECHECK_INTERVAL_MS = 20_000;
  let recheckTimer: ReturnType<typeof setInterval> | null = null;

  const syncQueuedLeads = () => {
    const offlineQueue = useOfflineLeadQueueStore();
    if(offlineQueue.hasPending) {
      offlineQueue.syncAll();
    }
  };

  // Trustworthy in the optimistic direction: the browser only fires this when it believes a
  // connection appeared, and a false positive costs one failed request rather than a session
  // stuck in offline mode.
  window.addEventListener('online', markOnline);

  // 'offline' is only a suspicion, so confirm it before putting the whole app into offline
  // mode. Same reasoning as the boot check below.
  window.addEventListener('offline', () => {
    verifyConnectivity();
  });

  // navigator.onLine can already be wrong when the app boots (that is the bug this replaces:
  // Chrome reported false browser-wide while every request returned 200), so it never sets
  // the state directly, it only asks for the same confirmation.
  if(!navigator.onLine) {
    verifyConnectivity();
  }

  // Recovery and queue draining hang off the state itself, so they run no matter what moved
  // it: a browser event, a probe, or simply a request that succeeded.
  watch(isOnline, (online) => {
    if(online) {
      if(recheckTimer) {
        clearInterval(recheckTimer);
        recheckTimer = null;
      }
      syncQueuedLeads();
      return;
    }
    if(!recheckTimer) {
      recheckTimer = setInterval(() => verifyConnectivity(), RECHECK_INTERVAL_MS);
    }
  });

  // A backgrounded tab has its timers throttled, so re-check on the way back in rather than
  // waiting out an interval the browser may have stretched to minutes.
  document.addEventListener('visibilitychange', () => {
    if(document.visibilityState === 'visible' && !isOnline.value) {
      verifyConnectivity();
    }
  });

  // Ensure SW controls the page (required for offline support)
  if('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      if(!navigator.serviceWorker.controller && registration.active) {
        registration.active.postMessage({ type: 'CLAIM_CLIENTS' });
      }
    });
  }

  // Sync any leftover pending items on app startup (deferred until app is mounted)
  nuxtApp.hook('app:mounted', () => {
    if(isOnline.value) {
      syncQueuedLeads();
    }
  });
});
